import { type Command, getMarkRange, getMarkType } from '@tiptap/core'
import { TextSelection } from '@tiptap/pm/state'
import { useRef, useEffect, useState, VFC, useCallback } from 'react'
import tippy, { type Instance as TippyInstance } from 'tippy.js'
import { useTextEditor } from '../../context'
import { ActionView } from './ActionView'
import { EditView } from './EditView'
import { HyperlinkManagerPlugin } from './plugin'
import * as styles from './styles'
import { HyperlinkManagerProps } from './types'

export const HyperlinkManager: VFC<HyperlinkManagerProps> = ({
  pluginKey = 'hyperlinkManager',
  readOnly = false,
}) => {
  const {
    editor,
    editorElement,
    theme,
    hyperlinkManager: { isOpen, set: setOpenState },
  } = useTextEditor()
  const elementRef = useRef<HTMLDivElement>(null)
  const tippyInstance = useRef<TippyInstance | null>(null)
  const shouldShow = useRef<boolean>(false)
  const [isEditing, setIsEditing] = useState(false)

  const getTippyInstance = useCallback(() => {
    if (!tippyInstance.current) {
      if (editorElement == null || !elementRef.current || readOnly) {
        return null
      }

      const handleShow = () => {
        const isLink =
          editor.isActive('link') || editor.isActive('embeddedResource')
        if (!isLink) {
          setIsEditing(true)
        }

        setOpenState(true)
      }
      const handleAfterShow = () => {
        if (shouldShow.current) {
          // Scroll current selection into view, if opening externally
          // Otherwise, the popover could appear to be hidden
          const { node } = editor.view.domAtPos(editor.state.selection.anchor)
          if (node instanceof Element) {
            node.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          }
        }
      }

      const handleHide = () => {
        setOpenState(false)
        setIsEditing(false)
      }

      const handleClickOutside = (instance: TippyInstance) => {
        instance.hide()
        handleHide()
      }

      tippyInstance.current = tippy(editorElement, {
        duration: 0,
        getReferenceClientRect: null,
        content: elementRef.current,
        interactive: true,
        trigger: 'manual',
        placement: 'bottom-start',
        hideOnClick: false,
        ignoreAttributes: true,
        offset: [0, 4],
        maxWidth: 366,
        arrow: false,
        onShow: handleShow,
        onShown: handleAfterShow,
        onHide: handleHide,
        onClickOutside: handleClickOutside,
        onBeforeUpdate: (instance, partialProps) => {
          // Reset editing state if popover position moved
          if ('getReferenceClientRect' in partialProps) {
            setIsEditing(false)
          }
        },
      })
    }

    return tippyInstance.current
  }, [editor, editorElement, setOpenState, readOnly])

  const hide = ({ moveCursor = true } = {}) => {
    tippyInstance.current?.hide()
    // Focus & move cursor to the end of the link
    // This avoids triggering the link menu again after an action
    if (moveCursor) {
      const moveCursorCmd: Command = ({ tr, state: { schema, selection } }) => {
        const linkRange = getMarkRange(
          selection.$from,
          getMarkType('link', schema)
        )

        if (linkRange) {
          tr.setSelection(TextSelection.create(tr.doc, linkRange.to))
        }

        return true
      }

      // Wrap in immediate setTimeout so that hide() completes first
      setTimeout(() => editor.chain().focus().command(moveCursorCmd).run(), 0)
    }
  }

  useEffect(() => {
    shouldShow.current = isOpen
  }, [editor, isOpen])

  useEffect(() => {
    if (!elementRef.current || editor.isDestroyed) {
      return
    }

    const plugin = HyperlinkManagerPlugin(pluginKey, {
      element: elementRef.current,
      externalOpenState: shouldShow,
      getTippy: getTippyInstance,
    })

    editor.registerPlugin(plugin)
    return () => {
      tippyInstance.current?.destroy()
      tippyInstance.current = null
      editor.unregisterPlugin(pluginKey)
    }
  }, [editor, pluginKey, getTippyInstance])

  return (
    <div
      ref={elementRef}
      style={{ visibility: isOpen ? 'visible' : 'hidden' }}
      css={[styles.popover({ theme }), 'texteditor-hyperlink-manager']}
    >
      <ActionView
        editor={editor}
        onClose={hide}
        onEdit={() => setIsEditing(true)}
        wrapperStyle={isEditing ? { display: 'none' } : undefined}
      />
      {isEditing && <EditView editor={editor} onClose={hide} />}
    </div>
  )
}
